<script setup lang="ts">
import {computed, onMounted, PropType, ref, watch} from 'vue';
import {
    AvailabilityState,
    CabPeriod,
    CabReportItem,
    CabUser,
    OnderwerpEnum_name,
    PeriodEnum,
    renderCabPeriod,
    renderTerseDate,
    Role,
    StatusEnum,
    StatusEnum_name,
} from 'common';
import {ElButton, ElCheckboxButton, ElCheckboxGroup, ElDialog, ElMessage, ElOption, ElSelect} from 'element-plus';
import {
    BackendError,
    listAllReporterReportItems,
    listObjects,
    listReporterReportItems,
    updateReportItem,
    updateReportItemAsAdmin,
} from '@/base/callBackend.js';
import {done, showHideLoading} from '@/base/loadingState.js';
import {localStorageGetJson, localStorageSetJson, ReportGroup, SelectGroupInfo} from '@/base/utils.js';
import SelectorGroup from '@/views/shared/SelectorGroup.vue';
import ReportItemStrip from '@/views/shared/ReportItemStrip.vue';
import KpiIcon from '@/views/icons/KpiIcon.vue';
import ExprIcons from '@/views/shared/ExprIcons.vue';
import WhiteCircleIcon from '@/views/icons/WhiteCircleIcon.vue';
import GreenCircleIcon from '@/views/icons/GreenCircleIcon.vue';
import OrangeCircleIcon from '@/views/icons/OrangeCircleIcon.vue';
import BlueCircleIcon from '@/views/icons/BlueCircleIcon.vue';
import PurpleCircleIcon from '@/views/icons/PurpleCircleIcon.vue';
import RedCircleIcon from '@/views/icons/RedCircleIcon.vue';
import GreenOrangeCircleIcon from '@/views/icons/GreenOrangeCircleIcon.vue';
import SelectAllIcon from '@/views/icons/SelectAllIcon.vue';
import SelectNoneIcon from '@/views/icons/SelectNoneIcon.vue';
import {stateForReporters} from '@/base/TMP_globalState.js';
import VerklaringDerSymbolen from '@/views/shared/VerklaringDerSymbolen.vue';

const props = defineProps({
    reportGroup: {
        type    : String as PropType<ReportGroup>,
        required: false,
    },
    isAdmin: {
        type   : Boolean,
        default: false,
    },
});

//============================================================================================================================================
const storageKeyPrefix = computed(() => props.isAdmin ? 'admin' : (props.reportGroup ?? 'reporter'));
const allRaw           = ref<CabReportItem[]>([]); // Raw data from backend (all statuses)
//
const selectedKwartalenList    = ref<string[]>([]);
const selectedInstellingenList = ref<string[]>([]);
const selectedThemasList       = ref<string[]>([]);
const selectedOnderwerpenList  = ref<string[]>([]);
//
const ADMIN_FILTER_VALUE       = '';
const allReporters             = ref<CabUser[]>([]);
const selectedReporterUuid     = ref<string>(localStorageGetJson('admin:selectedReporter') ?? ADMIN_FILTER_VALUE);

// Persist reporter filter selection
watch(selectedReporterUuid, (newVal) => {
    localStorageSetJson('admin:selectedReporter', newVal);
});

// Client-side status filtering (no more backend calls on status change)
const all = computed<CabReportItem[]>(() => {
    if (statusSet.value.length === 0) {
        return [];
    }
    const statusSetLookup = new Set(statusSet.value);
    return allRaw.value.filter(ri => statusSetLookup.has(ri.status!));
});

//
const allNaReporter     = computed<CabReportItem[]>(() => {
    if (!props.isAdmin || selectedReporterUuid.value === ADMIN_FILTER_VALUE) {
        return all.value;
    }
    const reporter = allReporters.value.find(r => r.uuid === selectedReporterUuid.value);
    if (!reporter) {
        return all.value;
    }
    const orgThemeCombis = reporter.getOrgThemeCombiUuids();
    return all.value.filter(ri => orgThemeCombis.some(([orgUuid, themeUuid]) =>
        ri.organization[0].uuid === orgUuid && ri.theme[0].uuid === themeUuid,
    ));
});
const allNaKwartalen    = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedKwartalenList.value);
    return allNaReporter.value.filter(ri => selectedSet.has(renderIndienMoment(ri)));
});
const allNaInstellingen = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedInstellingenList.value);
    return allNaKwartalen.value.filter(ri => selectedSet.has(ri.organization[0].uuid));
});
const allNaThemas       = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedThemasList.value);
    return allNaInstellingen.value.filter(ri => selectedSet.has(ri.theme[0].uuid));
});
const allNaOnderwerpen  = computed<CabReportItem[]>(() => {
    const selectedSet = new Set(selectedOnderwerpenList.value);
    return allNaThemas.value.filter(ri => selectedSet.has(ri.kpi[0].onderwerp?.toString() || ''));
});
//
const numReadyAll       = computed<number>(() => allNaOnderwerpen.value.filter(ri => ri.isSubmittable()).length);
const numTodoAll        = computed<number>(() => allNaOnderwerpen.value.filter(ri => !ri.isSubmittable()).length);

// Consolidated badge calculations - single pass through data for all maps
interface BadgeMaps {
    numAtKwartaal: Map<string, number>;
    numAtInstelling: Map<string, number>;
    numAtThema: Map<string, number>;
    numAtOnderwerp: Map<string, number>;
    numReadyAtKwartaal: Map<string, number>;
    numReadyAtInstelling: Map<string, number>;
    numReadyAtThema: Map<string, number>;
    numReadyAtOnderwerp: Map<string, number>;
    numTodoAtKwartaal: Map<string, number>;
    numTodoAtInstelling: Map<string, number>;
    numTodoAtThema: Map<string, number>;
    numTodoAtOnderwerp: Map<string, number>;
}

const badgeMaps                 = computed<BadgeMaps>(() => {
    const maps: BadgeMaps = {
        numAtKwartaal       : new Map(),
        numAtInstelling     : new Map(),
        numAtThema          : new Map(),
        numAtOnderwerp      : new Map(),
        numReadyAtKwartaal  : new Map(),
        numReadyAtInstelling: new Map(),
        numReadyAtThema     : new Map(),
        numReadyAtOnderwerp : new Map(),
        numTodoAtKwartaal   : new Map(),
        numTodoAtInstelling : new Map(),
        numTodoAtThema      : new Map(),
        numTodoAtOnderwerp  : new Map(),
    };

    // Pre-compute filter sets for efficiency
    const kwartaalSet   = new Set(selectedKwartalenList.value);
    const instellingSet = new Set(selectedInstellingenList.value);
    const themaSet      = new Set(selectedThemasList.value);

    // Single pass through allNaReporter for kwartaal maps
    for (const ri of allNaReporter.value) {
        const kwartaal = renderIndienMoment(ri);
        const isReady  = ri.isSubmittable();

        maps.numAtKwartaal.set(kwartaal, (maps.numAtKwartaal.get(kwartaal) || 0) + 1);
        if (isReady) {
            maps.numReadyAtKwartaal.set(kwartaal, (maps.numReadyAtKwartaal.get(kwartaal) || 0) + 1);
        } else {
            maps.numTodoAtKwartaal.set(kwartaal, (maps.numTodoAtKwartaal.get(kwartaal) || 0) + 1);
        }

        // Only process further if passes kwartaal filter
        if (!kwartaalSet.has(kwartaal)) {
            continue;
        }

        const instelling = ri.organization[0].uuid;
        maps.numAtInstelling.set(instelling, (maps.numAtInstelling.get(instelling) || 0) + 1);
        if (isReady) {
            maps.numReadyAtInstelling.set(instelling, (maps.numReadyAtInstelling.get(instelling) || 0) + 1);
        } else {
            maps.numTodoAtInstelling.set(instelling, (maps.numTodoAtInstelling.get(instelling) || 0) + 1);
        }

        // Only process further if passes instelling filter
        if (!instellingSet.has(instelling)) {
            continue;
        }

        const thema = ri.theme[0].uuid;
        maps.numAtThema.set(thema, (maps.numAtThema.get(thema) || 0) + 1);
        if (isReady) {
            maps.numReadyAtThema.set(thema, (maps.numReadyAtThema.get(thema) || 0) + 1);
        } else {
            maps.numTodoAtThema.set(thema, (maps.numTodoAtThema.get(thema) || 0) + 1);
        }

        // Only process further if passes thema filter
        if (!themaSet.has(thema)) {
            continue;
        }

        const onderwerp = ri.kpi[0].onderwerp?.toString() || '';
        maps.numAtOnderwerp.set(onderwerp, (maps.numAtOnderwerp.get(onderwerp) || 0) + 1);
        if (isReady) {
            maps.numReadyAtOnderwerp.set(onderwerp, (maps.numReadyAtOnderwerp.get(onderwerp) || 0) + 1);
        } else {
            maps.numTodoAtOnderwerp.set(onderwerp, (maps.numTodoAtOnderwerp.get(onderwerp) || 0) + 1);
        }
    }

    return maps;
});
//
const editIndex                 = ref<number>();
const cursorLine                = ref<number>();
const editReportItem            = computed<CabReportItem | undefined>(() => editIndex.value != undefined ? allNaOnderwerpen.value[editIndex.value] : undefined);
//
const isNotHeden                = ref<boolean>(!props.isAdmin && !!props.reportGroup && props.reportGroup !== ReportGroup.heden);
const dialogVisible             = ref<boolean>();
const indienenInProgress        = ref<boolean>(false);
const viewLoading               = ref<boolean>(true);
const allStatuses: StatusEnum[] = [StatusEnum.overdue, StatusEnum.open, StatusEnum.submitted, StatusEnum.accepted, StatusEnum.rejected, StatusEnum.future];
const statusSet                 = ref<StatusEnum[]>(initStatusSet());
const currentKwartaal           = renderCabPeriod(CabPeriod.of(new Date(), PeriodEnum.Q));

function initStatusSet(): StatusEnum[] {
    const key   = `${props.isAdmin ? 'admin' : (props.reportGroup ?? 'reporter')}:selectedStatuses`;
    const saved = localStorageGetJson(key);
    if (saved === undefined) {
        return [StatusEnum.open, StatusEnum.overdue];
    }
    return saved.filter((s: number) => allStatuses.includes(s));
}

// Status filter changes are now handled client-side - no backend reload needed
watch(statusSet, (newVal) => {
    localStorageSetJson(`${storageKeyPrefix.value}:selectedStatuses`, newVal);
});
onMounted(async () => {
    if (!props.isAdmin && stateForReporters.value === AvailabilityState.closed) {
        return;
    }
    showHideLoading(true);
    viewLoading.value = true;
    if (props.isAdmin) {
        const users        = await listObjects(CabUser);
        allReporters.value = users
            .filter(u => u.roles.includes(Role.reporter))
            .sort((a, b) => a.name.localeCompare(b.name));
    }
    await getAllFromBackend();
    viewLoading.value = false;
    showHideLoading(false);
});

async function getAllFromBackend() {
    try {
        showHideLoading(true);
        let cabReportItems: CabReportItem[];
        if (props.isAdmin) {
            // Fetch ALL statuses, filter client-side for instant status filter changes
            cabReportItems = await listObjects(CabReportItem);
        } else if (props.reportGroup) {
            cabReportItems = await listReporterReportItems(props.reportGroup);
        } else {
            // Fetch ALL statuses, filter client-side for instant status filter changes
            cabReportItems = await listAllReporterReportItems();
        }
        allRaw.value = cabReportItems.sort((a, b) => a.compare(b));
    } catch (e) {
        console.error(e);
        if (e instanceof BackendError) {
            ElMessage.error(`Error: ${e.details}`);
        }
    } finally {
        showHideLoading(false);
    }
}

//==================================================================================================
function kwartaalSelector(ri: CabReportItem): SelectGroupInfo {
    const x = renderIndienMoment(ri);
    return {
        name: x,
        id  : x,
    };
}

function instellingSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.organization[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function themaSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.theme[0];
    return {
        name: x.name,
        id  : x.uuid,
    };
}

function onderwerpSelector(ri: CabReportItem): SelectGroupInfo {
    const x = ri.kpi[0].onderwerp!;
    return {
        name: OnderwerpEnum_name(x),
        id  : x.toString(),
    };
}

//==================================================================================================
function getKwartaalBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return badgeMaps.value.numAtKwartaal.get(a);
    } else {
        return badgeMaps.value.numReadyAtKwartaal.get(a) ?? (getKwartaalExtraBadgeValue(a) && 0);
    }
}

function getKwartaalExtraBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtKwartaal.get(a);
    }
}

function getInstellingBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return badgeMaps.value.numAtInstelling.get(a);
    } else {
        return badgeMaps.value.numReadyAtInstelling.get(a) ?? (getInstellingExtraBadgeValue(a) && 0);
    }
}

function getInstellingExtraBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtInstelling.get(a);
    }
}

function getThemaBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return badgeMaps.value.numAtThema.get(a);
    } else {
        return badgeMaps.value.numReadyAtThema.get(a) ?? (getThemaExtraBadgeValue(a) && 0);
    }
}

function getThemaExtraBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtThema.get(a);
    }
}

function getOnderwerpBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return badgeMaps.value.numAtOnderwerp.get(a);
    } else {
        return badgeMaps.value.numReadyAtOnderwerp.get(a) ?? (getOnderwerpExtraBadgeValue(a) && 0);
    }
}

function getOnderwerpExtraBadgeValue(a: string): number | undefined {
    if (isNotHeden.value) {
        return undefined;
    } else {
        return badgeMaps.value.numTodoAtOnderwerp.get(a);
    }
}

//============================================================================================================================================
async function onIndienen() {
    indienenInProgress.value = true;
    try {
        const submittableItems = allNaOnderwerpen.value.filter(ri => ri.isSubmittable());
        const updateFn         = props.isAdmin ? updateReportItemAsAdmin : updateReportItem;

        // Update status locally and prepare update promises
        const updatePromises = submittableItems.map(ri => {
            ri.status = StatusEnum.submitted;
            return updateFn(ri);
        });

        // Execute all updates in parallel
        await Promise.all(updatePromises);

        // Refresh data from backend instead of full page reload
        await getAllFromBackend();
    } catch (e) {
        console.error('Error submitting items:', e);
        if (e instanceof BackendError) {
            ElMessage.error(`Error: ${e.details}`);
        }
    } finally {
        indienenInProgress.value = false;
    }
}

//============================================================================================================================================
function renderIndienMoment(ri: CabReportItem) {
    let period: CabPeriod;
    if (ri.isMijlpaal) {
        period = CabPeriod.of(ri.meetMoment!, PeriodEnum.Q).previousPeriod();
    } else {
        period = ri.period[0].qPeriod();
    }
    return renderCabPeriod(period);
}

function dynLineClass(i: number): string {
    const onEvenLine   = i % 2 == 0;
    const onCursorLine = i == cursorLine.value;
    return onEvenLine ? onCursorLine ? 'bg-green-300' : 'bg-green-200' : onCursorLine ? 'bg-green-300' : 'bg-green-100';
}

function getBadgeColor(): string {
    return isNotHeden.value ? 'blue' : 'green';
}

function getExtraBadgeColor(): string | undefined {
    return isNotHeden.value ? undefined : 'orange';
}

function getStatusIcon(ri: CabReportItem) {
    switch (ri.status) {
        case StatusEnum.future:
            return WhiteCircleIcon;
        case StatusEnum.open:
        case StatusEnum.overdue:
            return ri.isSubmittable() ? GreenCircleIcon : OrangeCircleIcon;
        case StatusEnum.submitted:
            return BlueCircleIcon;
        case StatusEnum.accepted:
            return PurpleCircleIcon;
        case StatusEnum.rejected:
            return RedCircleIcon;
    }
}

function getStatusFilterIcon(status: StatusEnum) {
    switch (status) {
        case StatusEnum.future:
            return WhiteCircleIcon;
        case StatusEnum.open:
        case StatusEnum.overdue:
            return GreenOrangeCircleIcon;
        case StatusEnum.submitted:
            return BlueCircleIcon;
        case StatusEnum.accepted:
            return PurpleCircleIcon;
        case StatusEnum.rejected:
            return RedCircleIcon;
    }
}

</script>

<template>
    <div class="w-full h-full relative">

        <div class="pt-5">
            <template v-if="(isAdmin || stateForReporters!==AvailabilityState.closed) && !viewLoading">
                <div v-if="isAdmin" class="flex flex-row px-10 w-full h-16">
                    <div class="w-52 flex-shrink-0">
                        <div class="pl-2 pr-4 py-2 my-1 text-right font-thin flex items-center justify-end gap-1">
                            <span>reporter</span>
                        </div>
                    </div>
                    <div class="flex-1 py-2">
                        <el-select
                            v-model="selectedReporterUuid"
                            class="w-64"
                            clearable
                            placeholder="alle reporters"
                        >
                            <el-option
                                v-for="reporter in allReporters"
                                :key="reporter.uuid"
                                :value="reporter.uuid"
                                :label="reporter.name"
                            />
                        </el-select>
                    </div>
                </div>
                <div v-if="isAdmin || !reportGroup" class="flex flex-row px-10 w-full h-16">
                    <div class="w-52 flex-shrink-0">
                        <div class="pl-2 pr-4 py-2 my-1 text-right font-thin flex items-center justify-end gap-1">
                            <span>statussen</span>
                            <div class="flex flex-col">
                                <div
                                    @click="statusSet = [...allStatuses]"
                                    :class="statusSet.length === allStatuses.length ? 'cursor-default opacity-30' : 'cursor-pointer'"
                                    title="Selecteer alles"
                                >
                                    <SelectAllIcon/>
                                </div>
                                <div
                                    @click="statusSet = []"
                                    :class="statusSet.length === 0 ? 'cursor-default opacity-30' : 'cursor-pointer'"
                                    title="Selecteer niets"
                                >
                                    <SelectNoneIcon/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-1 py-2">
                        <el-checkbox-group v-model="statusSet" class="flex">
                            <el-checkbox-button
                                v-for="status in allStatuses"
                                :key="status"
                                :value="status"
                                border
                            >
                                <span class="flex items-center gap-1">
                                    <component :is="getStatusFilterIcon(status)"/>
                                    {{ StatusEnum_name(status) }}
                                </span>
                            </el-checkbox-button>
                        </el-checkbox-group>
                    </div>
                </div>
                <SelectorGroup
                    :all="allNaReporter"
                    :names="['kwartaal','kwartalen']"
                    :local-storage-name="`${storageKeyPrefix}:selectedKwartalen`"
                    :select-field="kwartaalSelector"
                    v-model:selected-list="selectedKwartalenList"
                    :get-badge-value="getKwartaalBadgeValue"
                    :get-extra-badge-value="getKwartaalExtraBadgeValue"
                    :badge-color="getBadgeColor()"
                    :extra-badge-color="getExtraBadgeColor()"
                    :highlight-id="currentKwartaal"
                />
                <SelectorGroup
                    :all="allNaReporter"
                    :names="['instelling','instellingen']"
                    :local-storage-name="`${storageKeyPrefix}:selectedInstellingen`"
                    :select-field="instellingSelector"
                    v-model:selected-list="selectedInstellingenList"
                    :get-badge-value="getInstellingBadgeValue"
                    :get-extra-badge-value="getInstellingExtraBadgeValue"
                    :badge-color="getBadgeColor()"
                    :extra-badge-color="getExtraBadgeColor()"
                />
                <SelectorGroup
                    :all="allNaReporter"
                    :names="['thema','themas']"
                    :local-storage-name="`${storageKeyPrefix}:selectedThemas`"
                    :select-field="themaSelector"
                    v-model:selected-list="selectedThemasList"
                    :get-badge-value="getThemaBadgeValue"
                    :get-extra-badge-value="getThemaExtraBadgeValue"
                    :badge-color="getBadgeColor()"
                    :extra-badge-color="getExtraBadgeColor()"
                />
                <SelectorGroup
                    :all="allNaReporter"
                    :names="['onderwerp','onderwerpen']"
                    :local-storage-name="`${storageKeyPrefix}:selectedOnderwerpen`"
                    :select-field="onderwerpSelector"
                    v-model:selected-list="selectedOnderwerpenList"
                    :get-badge-value="getOnderwerpBadgeValue"
                    :get-extra-badge-value="getOnderwerpExtraBadgeValue"
                    :badge-color="getBadgeColor()"
                    :extra-badge-color="getExtraBadgeColor()"
                />
            </template>
        </div>

        <div class="flex py-3 px-10">
            <!-- Content -->
            <div class="w-full border-8 border-white" v-if="done && !viewLoading">
                <div class="overflow-x-auto">
                    <div v-if="all.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - er staan op dit moment geen items open -
                    </div>
                    <div v-else-if="allNaOnderwerpen.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - lege selectie -
                    </div>
                    <table class="table-auto border-collapse border w-full cursor-pointer">
                        <tbody>
                        <tr v-for="(ri,i) in allNaOnderwerpen"
                            :key="ri.uuid"
                            class=""
                            :class="dynLineClass(i)"
                            @mouseenter="cursorLine = i"
                            @mouseleave="cursorLine = -1"
                            @click="editIndex = i; dialogVisible = true"
                        >
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-2">
                                <component :is="getStatusIcon(ri)"/>
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ renderIndienMoment(ri) }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.organization[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.theme[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ OnderwerpEnum_name(ri.kpi[0].onderwerp) }}
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-3">
                                <KpiIcon :kpi="ri.kpi[0]"/>
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-0 pr-3 font-bold w-full">
                                {{ ri.kpi[0].name }}
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-0 pr-3 font-bold w-full">
                                <ExprIcons :kpi="ri.kpi[0]"/>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="flex flex-row pb-2 pl-10 cursor-pointer"
             v-if="!isAdmin && stateForReporters===AvailabilityState.write && all.length!=0">
            <el-button type="primary"
                       @click="onIndienen()"
                       :disabled="indienenInProgress || numReadyAll==0"
                       v-if="numReadyAll"
                       class="w-40"
            >
                {{ numReadyAll == 1 ? '1 Item Indienen' : `${numReadyAll} Items Indienen` }}
            </el-button>
            <div class="ml-2 mt-1 flex-row flex" v-if="numReadyAll!=0">
                Er {{ numReadyAll == 1 ? `staat 1 item` : `staan ${numReadyAll} items` }}
                <GreenCircleIcon class="mx-1"/>
                klaar om ingediend te worden.
            </div>
            <div class="ml-2 mt-1 flex-row flex" v-if="numTodoAll!=0">
                Er {{ numTodoAll == 1 ? `kan nog 1 item` : `kunnen nog ${numTodoAll} items` }}
                <OrangeCircleIcon class="mx-1"/>
                ingevuld worden.
            </div>
        </div>

        <VerklaringDerSymbolen/>

    </div>

    <el-dialog
        v-model="dialogVisible"
        width="90%"
        align-center
        destroy-on-close
        :show-close="false"
        v-if="editReportItem" :draggable="true"
    >
        <template #header>
            <div class="flex flex-row items-center text-lg">
                <div class="cursor-default mx-2 whitespace-nowrap">
                    <component :is="getStatusIcon(editReportItem)"/>
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ editReportItem.organization[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ editReportItem.theme[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ OnderwerpEnum_name(editReportItem.kpi[0].onderwerp) }}
                </div>
                <KpiIcon :kpi="editReportItem.kpi[0]" class="ml-2"/>
                <div class="font-bold flex-wrap">
                    {{ editReportItem.kpi[0].name }}
                </div>
                <div class="flex-1">
                    <!--filler-->
                </div>

                <div class="mx-2 px-4 bg-gray-200 whitespace-nowrap">
                    {{ editReportItem.isMijlpaal ? `per ${renderTerseDate(editReportItem.meetMoment)}` : `over ${renderCabPeriod(editReportItem.period[0])}`
                    }}
                </div>
            </div>
        </template>

        <ReportItemStrip
            :flag-omissions="true"
            :item="editReportItem"
            :is-admin="isAdmin"
            @refresh-reports="getAllFromBackend"
        />

    </el-dialog>
</template>

<style>
/*noinspection CssUnusedSymbol*/
.el-button:disabled {
    @apply bg-gray-400 text-gray-200;
}
</style>
